import { ArrowRightIcon, VarietalIcon } from "@vinomofo/icons";
import {
  Award,
  EventSubType,
  EventType,
  ResponsiveValue,
  ResponsiveVariant,
  StackSpace,
  StorefrontImage,
  Trackable,
  UrgencyMessage,
} from "../../types";
import {
  Box,
  BoxProps,
  Button,
  Card,
  DiscountBadge,
  IconLabel,
  PriceButton,
  Stack,
  Text,
  Tooltip,
} from "@vinomofo/components";
import { CountdownTimer, calculateTimeLeft } from "./CountdownTimer";
import { SpiritsGlassIcon, LocationIcon } from "./VarietalAndLocationIcon";
import { Tiers, VinoUser } from "../../contexts/auth/state";
import { VinoDataImage, VinoDataOffer } from "../../types/vino-data-types";
import styled, { keyframes } from "styled-components";
import { useEffect, useMemo, useState } from "react";

import { BadgeStack } from "../badge-stack";
import { BellToggleV2 } from "../bell-togglev2";
import { COLORS } from "../../styles";
import { InsertData } from "../../../api/src/services/dynamodb";
import { LocationPinIcon } from "@vinomofo/icons";
import OfferCardImage from "./OfferCardImage";
import { OfferCtaMessage } from "../offer-cta-message";
import { PriceDrop } from "../price-drop";
import { Roundel } from "../roundel";
import { StaticImage } from "gatsby-plugin-image";
import { colorMap } from "../wine-notes";
import { determineTierIndex } from "../../utils/loyalty";
import { discountDisplays } from "../../utils/constants";
import isBrowser from "../../utils/is-browser";
import { mockTrackEvent } from "../../utils/event-tracking";
import { navigate, Link } from "gatsby";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useLoyalty } from "../../hooks/account/use-loyalty";
import { isVinomofo, isWhiskymofo } from "../../utils/is-page";

export interface AddToCartOffer extends Trackable {
  sku?: { [key: string]: any };
  bigcommerceId?: string;
  productId?: string;
  cardImage?: any | VinoDataImage | StorefrontImage | { url: string };
  offerType?: string;
  name?: string;
  route?: {
    slug?: string;
  };
  [key: string]: any;
}

export interface OfferCardProps extends Trackable {
  productId: string;
  awards: Array<Award>;
  discountPercentage: number;
  discountDollarValue: number;
  discountDisplay: string;
  startsAt?: Date;
  endsAt?: Date;
  price: number;
  rrp?: number;
  unitRRP?: number;
  packRRP?: number;
  showPriceDrop: boolean;
  showCountdown?: boolean;
  showAwardBadges?: boolean;
  hideRRP?: boolean;
  hideVIMExclusive?: boolean;
  priceDescription?: string;
  region: string;
  horizontalBadges?: boolean;
  roundelsImage: any;
  cardImage: any;
  urgencyMessage?: UrgencyMessage;
  showDiscountBadge: boolean;
  isMixed?: boolean;
  is_wine?: boolean;
  isSpirit?: boolean;
  isSoldOut?: boolean;
  tagLine?: string;
  subTagLine?: string;
  skeleton?: boolean;
  variant?: ResponsiveVariant;
  onClick?: () => void;
  backgroundColor?: string;
  eventPage?: boolean;
  name?: string;
  offerType?: string;
  route: {
    slug: string;
    path: string;
  };
  brand: string;
  objectID?: string;
  category?: string;
  currency?: string;
  discount?: string;
  inventoryStatus: string;
  url: string;
  packSize: string;
  country: string;
  offerStyle: string;
  offerVariant: string;
  varietal: string;
  preservatives: string;
  vintage: string;
  sku: { [key: string]: any };
  wideHeroImage?: StorefrontImage;
  detailsImageWide?: StorefrontImage;
  moreBtnLink?: string;
  disableAllDisplayFlags?: boolean;
  secret?: boolean;
  isRecommended?: boolean;
  columns?: number;
  tiersEligible: Tiers[];
  styles: string;
  bottleShape?: string;
  offerJson: VinoDataOffer;
  isMyOffer: boolean;
  queryID: string;
  indexId?: number;
  tags?: [string];
  productCardLayout?: "One Column" | "Two Column" | null;
  cardBackgroundImage?: any;
  isOffersCollection?: boolean;
}

const StyledDiscountBadge = styled(DiscountBadge)`
  > span:first-of-type {
    font-size: 22px;
    font-weight: 900;
  }

  > p:first-of-type {
    font-size: 10px;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;

    > span:first-of-type {
      font-size: 20px;
    }

    > p:first-of-type {
      font-size: 9px;
    }
  }
`;

const StyledStack = styled(Stack)`
  @media (min-width: 768px) and (max-width: 979px) {
    margin-top: 21px;
  }

  @media (min-width: 979px) and (max-width: 1198px) {
    margin-top: 25px;
  }

  @media (max-width: 524px) {
    margin-top: 20px;
  }
`;

export const CardLink = styled(Link)<{
  skeleton: boolean;
  color?: string;
}>`
  width: 100%;
  text-decoration: none;
  pointer-events: ${({ skeleton }) => (skeleton ? "none" : "auto")};
  cursor: ${({ skeleton }) => (skeleton ? "auto" : "pointer")};
  color: ${({ color, theme }) => (color ? color : theme.colors.text.heading)};
  transition: color 0.3s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  ::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

export const CardAnchor = styled.a<{
  skeleton: boolean;
  color?: string;
}>`
  width: 100%;
  text-decoration: none;
  pointer-events: ${({ skeleton }) => (skeleton ? "none" : "auto")};
  cursor: ${({ skeleton }) => (skeleton ? "auto" : "pointer")};
  color: ${({ color, theme }) => (color ? color : theme.colors.text.heading)};
  transition: color 0.3s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.mofoPrimary};
  }

  ::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

export const OfferTimer = styled(CountdownTimer)<{
  isExpired: boolean;
  isSpirit: boolean;
  urgencyMessage?: boolean;
  isLessThan24Hours?: boolean;
}>`
  position: ${({ isSpirit }) => (isSpirit ? "absolute" : "relative")};;
  height: ${({ isSpirit }) => (isSpirit ? "27px" : 0)};
  flex-direction: ${({ isSpirit }) => (isSpirit ? "row" : "column")};
  top: ${({ isSpirit }) => (isSpirit ? "-23px" : "-36px")};
  opacity: ${({ isExpired, isSpirit }) => (isExpired && !isSpirit ? 0.75 : 1)};
  ${({ isExpired }) => isExpired && "filter: grayscale(100%);"}

  ${({ isSpirit, urgencyMessage, isLessThan24Hours, theme }) =>
    isSpirit &&
    `
    width: 100%;
    border-radius: 5px 5px 0px 0px;
    align-items: anchor-center;
    justify-content: space-between;
    margin-bottom: 2px;
    > div:nth-of-type(1) {
      background: none;
      span {
        font-size: 13px;
        font-family: ${theme.fonts.heading};
        font-weight: 600;
      }

      @media (max-width: 767px) {
        padding: 0 10px;

        span {
          font-size: 12px !important;
        }
      }
      
    }

    > div:nth-of-type(2) {
      display: block;
      margin-right: ${
        urgencyMessage ? (isLessThan24Hours ? "6px" : "13px") : "15px"
      };
    }

    div:nth-of-type(2) {
      div:nth-of-type(2) {
        min-width: 25px;

        > div {
          > div:not(:last-child)::after {
            content: ':';
            position: absolute;
            color: white;
            z-index: 100;
            font-size: 13px;
            @media (max-width: 767px) {
              font-size: 11px;
            } 
          }

          > div:nth-of-type(1)::after {
            right: -6px;
          }

          > div:nth-of-type(2)::after {
            right: 0;
          }
        }
      }

      > div {
        background: none;
        border: none;

        > div {
          position: relative;
          height: auto;
        }

        span {
          color: white;
          font-size: 13px;
          font-family: ${theme.fonts.heading};
          font-weight: 500;
        }

        > div > span {
          display: none;
        }
      }
    }

     @media (max-width: 980px) {

      > div:nth-of-type(1) {
        padding: 0 15px;
        margin-bottom: 2px;
      }
      > div {
        span {
          font-size: 12px !important;
        }
      }
    }

    @media (max-width: 767px) {
      > div:nth-of-type(2) {
        margin-right: 8px;
      }

      > div:nth-of-type(1) {
        padding: 0 10px;
      }
      > div {
        span {
          font-size: 12px !important;
        }
      }
    }

    @media (max-width: 450px) {
      div:nth-of-type(2) {
        div:nth-of-type(2) {
          min-width: 24px;

          > div > div:nth-of-type(1)::after {
            right: -7px;
          }

          > div:nth-of-type(2)::after {
            right: 0;
          }
        }
      }
      > div:nth-of-type(1) {
        padding: 0px 0px 0px 10px;
      }
      > div {
        display: block;
        margin-bottom: 2px;

        > div {
          span {
            font-size: 11px !important;
          }
        }
      }
    }
  `}

  @media ${({ theme }) => theme.mq.max.tablet} {
   top: ${({ isSpirit }) => (isSpirit ? "-22px" : "-31px")};
   height: ${({ isSpirit }) => (isSpirit ? "25px" : "27px")};
    div > div {
      min-width: auto;
      > div {
        height: auto;
        width:  ${({ isSpirit }) => (isSpirit ? "50px" : "100px")};
        > div {
          line-height: 1;
          width: ${({ isSpirit }) => (isSpirit ? "10px" : "30px")};
          text-align: center;
          > span {
            font-size: 7px;
            line-height: 1;    
            margin-bottom: 2px;     
          }
          > div > span {
              font-size: 10px;
            }
          }
          p {
            strong {
              font-size: 11px;
            }
            font-size: 8px;
            line-height: 1.8;
          }
        }
      }
    }
  }
  [class*="TimeDisplay__Container"] {
    border-radius: 12px;

    > div {
      width: 24%;
    }

    [class*="TimeDisplay__TimeLabel"] {
      width: 30px;
      font-size: 8px;
    }
  }

  ${({ isSpirit, theme }) =>
    isSpirit &&
    `
    [class*="TimeDisplay__Container"] {
      border-radius: 0;
      width: auto !important;

      > div {
        width: 22px;
        text-align: center;
        position: relative;
        display: block;

        @media ${theme.mq.max.mobile} {
          width: 22px;
        }

        > div {
          width: 24px;
          position: relative;
          display: block;
          margin: auto;

          @media ${theme.mq.max.mobile} {
            width: 22px;
          }

          span {
            width: 100%;
            color: white;
            font-family: ${theme.fonts.heading};
            font-size: 13px;
            display: inline-block;
          }
        }

        &:after {
          content: ":";
          position: absolute;
          top: 1px;
          right: -2px !important;
          font-size: 18px;

          @media ${theme.mq.max.mobile} {
            top: 1px !important;
          }
        }

        &:last-child {
          &:after {
            display: none;
          }
        }
      }
    }
  `}
`;

const BoxExtended = styled(Box)`
  .gatsby-image-wrapper {
    position: absolute !important;
  }
`;

const StyledTitle = styled(Box)`
  margin-bottom: 20px;
  margin-top: auto;
  @media ${({ theme }) => theme.mq.max.desktop} {
    min-height: 32px;
  }
`;

const StyledBoxWrapper = styled(Box)`
  > div:nth-child(1) {
    p {
      margin-right: 4px;
    }
  }
`;

const StyledBMLogo = styled.div<{ columns: number }>`
  width: 45px;
  position: absolute;
  z-index: 2;
  right: 22px;
  bottom: 22px;
  @media screen and (max-width: 1040px) and (min-width: 980px) {
    width: 40px;
    bottom: 10px;
    right: 20px;
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    right: ${({ columns }) => (columns === 4 ? "12px" : "24px")};
    bottom: 15px;
    width: 40px;
  }

  @media ${({ theme }) => theme.mq.max.mobile} {
    width: 36px;
    right: 14px;
    bottom: 12px;
  }
`;

const StyledIconLogo = styled.div<{ columns: number }>`
  width: 60px;
  position: absolute;
  z-index: 2;
  right: 14px;
  bottom: 10px;
  @media screen and (max-width: 1040px) and (min-width: 980px) {
    width: 60px;
    bottom: 5px;
    right: 10px;
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    right: ${({ columns }) => (columns === 4 ? "12px" : "20px")};
    bottom: 0;
    width: 50px;
  }

  @media ${({ theme }) => theme.mq.max.mobile} {
    width: 36px;
    right: 14px;
    bottom: 0;
  }
`;

const StyledBadgeStack = styled(BadgeStack)<{ columns: number }>`
  @media ${({ theme }) => theme.mq.max.mobile} {
    margin-left: ${({ columns }) => (columns === 4 ? "50px" : "")};
    margin-top: ${({ columns }) => (columns === 4 ? "25px" : "")};
    > div {
      margin-top: ${({ columns }) => (columns === 4 ? "-16px" : "")};
    }

    img {
      width: ${({ columns }) => (columns === 4 ? "40px" : "")};
      height: ${({ columns }) => (columns === 4 ? "40px" : "")};
    }
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    margin-left: ${({ columns }) => (columns === 4 ? "42px" : "")};
    margin-top: ${({ columns }) => (columns === 4 ? "25px" : "")};
    > div {
      margin-top: ${({ columns }) => (columns === 4 ? "-18px" : "")};
    }

    img {
      width: ${({ columns }) => (columns === 4 ? "40px" : "")};
      height: ${({ columns }) => (columns === 4 ? "40px" : "")};
    }
  }

  @media screen and (max-width: 979px) and (min-width: 769px) {
    margin-left: ${({ columns }) => (columns === 4 ? "40px" : "")};
    margin-top: ${({ columns }) => (columns === 4 ? "25px" : "")};
    > div {
      margin-top: ${({ columns }) => (columns === 4 ? "-32px" : "")};
    }
    img {
      width: ${({ columns }) => (columns === 4 ? "40px" : "")};
      height: ${({ columns }) => (columns === 4 ? "40px" : "")};
    }
  }

  @media screen and (max-width: 1100px) and (min-width: 980px) {
    margin-left: ${({ columns }) => (columns === 4 ? "65px" : "55px")};
    margin-top: 25px;
    > div {
      margin-top: -18px;
    }
    img {
      width: 50px;
      height: 50px;
    }
  }
`;

const sizeValues = ["sm", "md", "lg"] as const;
type Size = typeof sizeValues[number];

const getCardContentPadding = (variant: ResponsiveVariant) => {
  if (variant === "md" || variant === "lg") return 20;
  if (variant === "sm") return 20;

  return [10, 20];
};

const getNameFontSize = (variant: ResponsiveVariant) => {
  if (variant === "md" || variant === "lg") return 2;
  if (variant === "sm") return 0;

  return [0, 2];
};

const getDescriptionFontSize = (variant: ResponsiveVariant) => {
  if (variant === "md" || variant === "lg") return "12px";
  if (variant === "sm") return "10px";

  return ["10px", "12px"];
};

const getBadgeContainerPadding = (variant: ResponsiveVariant) => {
  if (variant === "md" || variant === "lg") return "16px";
  if (variant === "sm") return "9px";

  return ["9px", "16px"];
};

const getBadgeMargin = (variant: ResponsiveVariant) => {
  if (variant === "md" || variant === "lg") return "-24px";
  if (variant === "sm") return "-16px";

  return ["-16px", "-24px"];
};

const getButtonPadding = (
  variant: ResponsiveVariant
): ResponsiveValue<string> => {
  if (variant === "md" || variant === "lg") return "16px";
  if (variant === "sm") return "10px";

  return ["10px", "16px"];
};

const getButtonStackSpace = (
  variant: ResponsiveVariant
): ResponsiveValue<StackSpace> => {
  if (variant === "md" || variant === "lg") return 2;
  if (variant === "sm") return 1;

  return [1, 2];
};

const getButtonSize = (variant: ResponsiveVariant): ResponsiveValue<Size> => {
  if (variant === "md" || variant === "lg") return "md";
  if (variant === "sm") return "sm";

  return ["sm", "md"];
};

// Loading Bar Animation
const loadingAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const LoadingBarContainer = styled.div`
  position: absolute;
  top: 0px;
  left: 10px;
  width: 215px;
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 4px;
`;

const LoadingBar = styled.div`
  height: 100%;
  width: 100%;
  background-color: #77b204;
  border-radius: 4px;
  animation: ${loadingAnimation} 3s linear reverse forwards;
  transform-origin: right;
`;

const StyledCard = styled(Card)<{ isSoldOut: boolean; isSpirit: boolean }>`
  img,
  svg {
    filter: ${({ isSoldOut }) =>
      isSoldOut ? "grayscale(100%)" : "none"} !important;
  }

  [class*="PriceButton__SoldoutBtn"] {
    font-weight: 500;
    @media ${({ theme }) => theme.mq.min.tablet} {
      font-size: 15px;
    }
  }

  * img,
  * svg,
  * button,
  * [class*="DiscountBadge__"] {
    filter: ${({ isSoldOut }) =>
      isSoldOut ? "grayscale(100%)" : "none"} !important;
    opacity: ${({ isSoldOut }) => (isSoldOut ? "0.75" : "1")};
  }
`;

export const OfferCard = (props: OfferCardProps & BoxProps): JSX.Element => {
  const {
    productId,
    awards,
    discountPercentage,
    discountDollarValue,
    discountDisplay,
    price,
    unitRRP,
    packRRP,
    startsAt,
    endsAt,
    showPriceDrop,
    showCountdown,
    showAwardBadges,
    hideRRP,
    hideVIMExclusive,
    priceDescription,
    region,
    horizontalBadges,
    roundelsImage,
    cardImage,
    urgencyMessage,
    showDiscountBadge,
    isMixed = false,
    isSoldOut = false,
    skeleton = false,
    variant = "responsive",
    trackEvent = mockTrackEvent,
    backgroundColor,
    eventPage,
    route,
    name,
    offerType,
    sku,
    is_wine,
    isSpirit,
    wideHeroImage,
    detailsImageWide,
    moreBtnLink,
    disableAllDisplayFlags = false,
    secret,
    varietal,
    category,
    isRecommended,
    columns,
    tiersEligible,
    styles,
    bottleShape,
    tags,
    offerJson,
    isMyOffer,
    queryID,
    indexId,
    isOffersCollection,
    ...rest
  } = props;

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(new Date(endsAt)));
  const isExpired = endsAt ? timeLeft === null : false;

  const { loyalty, showLoyalty } = useLoyalty();
  const { addItem, isLoading } = useCart();
  const {
    user,
    addToFavourites,
    removeToFavourites,
    favourites,
    fetchingFavourites,
  } = useAuth();
  const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
  const [productLoading, setProductLoading] = useState({});
  const [isTierEligible, setIsTierEligible] = useState(false);
  const firstStyle = styles?.[0]?.split(",")[0];
  const customerId =
    userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];
  const vmWmSiteName = process.env.GATSBY_VM_WM_SITE_NAME;
  const vmWmAppUrl = vmWmSiteName ? process.env.GATSBY_VM_WM_APP_URL : "";

  const favouriteUserId_local = isBrowser()
    ? localStorage.getItem("favouriteId")
    : null;

  const favouriteUserId = useMemo(() => {
    return user?.email ? user.email : favouriteUserId_local;
  }, [user?.email, favouriteUserId_local]);

  const favouriteKey = useMemo(() => {
    return `${favouriteUserId}_KEY_${offerJson?.objectID || offerJson?.id}`;
  }, [favouriteUserId, offerJson, isMyOffer, favourites]);

  const [isInFavourites, setIsInFavourites] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(false);

  useEffect(() => {
    if (favourites !== undefined && !fetchingFavourites) {
      const ticked = favourites?.some((item) => item.id === favouriteKey);
      setIsInFavourites(ticked);
    }
  }, [favourites, fetchingFavourites, favouriteUserId]);

  const handleSubscription = async () => {
    await addToFavourites({
      user_id: favouriteUserId,
      offerJson: offerJson,
      wine_name: offerJson?.wines?.[0].name || "no-name",
      sku_code: offerJson.skuCode || "no-code",
      offer_name: offerJson?.name || "no-name",
      product_id: offerJson.objectID || offerJson.id,
    } as InsertData);
  };

  const handleUnsubscription = () => {
    if (isInFavourites && favouriteKey) {
      removeToFavourites(favouriteKey);
    }
  };

  useEffect(() => {
    if (user) {
      const { tier } = determineTierIndex(
        user as VinoUser,
        showLoyalty,
        loyalty?.loyalty_tier
      );

      const includesUserTier = tiersEligible?.includes(
        tier?.toUpperCase() as Tiers
      );
      const includesALLTier = tiersEligible?.includes("ALL" as Tiers);
      setIsTierEligible(
        includesUserTier && !includesALLTier && !hideVIMExclusive
      );
    }
  }, [loyalty, tiersEligible, user]);

  const handleAddButtonClick = (e) => {
    if (!isSoldOut) {
      setProductLoading((oldState) => ({ ...oldState, [productId]: true }));

      addItem(productId, props, customerId, "Offer Card", () => {
        setProductLoading((oldState) => ({ ...oldState, [productId]: false }));
      });
    } else {
      navigate(linkUrl);
    }
  };

  const rrp = isMixed ? packRRP : unitRRP;
  const isLarge = variant === "md" || variant === "lg";
  const skeletonColor = "divider";
  const cardImageBackgroundColor = backgroundColor || "";

  // useEffect(() => {
  //   if (productLoading[productId] && !isLoading)
  //     setProductLoading((oldState) => ({ ...oldState, [productId]: false }));
  // }, [isLoading, productLoading, productId]);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft(new Date(endsAt)));
  }, [endsAt]);

  let linkUrl = route?.path;

  if (isVinomofo && isSpirit) {
    linkUrl = vmWmAppUrl + route?.path;
  } else if (isWhiskymofo && !isSpirit) {
    linkUrl = vmWmAppUrl + route?.path;
  }
  linkUrl = user && user.email ? `${linkUrl}?sso=1` : linkUrl;

  let priceButtonColor = "white";
  let priceButtonBGColor =
    urgencyMessage && COLORS[urgencyMessage.backgroundColor];

  if (isTierEligible) {
    priceButtonBGColor = COLORS.black;
    priceButtonColor = COLORS.mofoGreen;
  }

  if (isWhiskymofo) {
    priceButtonBGColor = COLORS.gold;
    priceButtonColor = COLORS.white;
  }

  const getDisplayFlagMessage = (
    message?: UrgencyMessage,
    isSpirit?: boolean
  ): UrgencyMessage | undefined => {
    if (!message) return undefined;
    if (!isSpirit) return message;

    const isGoldHighlight = ["ALMOST_GONE", "SELLING_FAST"].includes(
      message.status
    );
    return {
      ...message,
      backgroundColor: isGoldHighlight ? COLORS.black : message.backgroundColor,
      textColor: isGoldHighlight ? COLORS.gold : COLORS.white,
    };
  };

  const shouldShowTop =
    isSoldOut ||
    !(showCountdown && endsAt) ||
    (showCountdown && endsAt && isWhiskymofo);
  const shouldShowBottom = !shouldShowTop;
  const borderRadius = shouldShowTop ? "5px 5px 0 0" : "0 0 5px 5px";
  const positionStyles = {
    top: shouldShowTop ? "-22px" : "",
    bottom: shouldShowBottom ? "-20px" : "",
  };
  const formattedDisplayFlagMessage = getDisplayFlagMessage(
    urgencyMessage,
    isWhiskymofo
  );

  return (
    <StyledCard
      data-key={offerJson.id}
      variant={isLarge ? "hard" : "default"}
      {...rest}
      backgroundColor="white"
      key={favouriteKey}
      isSoldOut={isSoldOut}
      isSpirit={isWhiskymofo}
    >
      {pendingRemoval && (
        <LoadingBarContainer>
          <LoadingBar />
        </LoadingBarContainer>
      )}
      {!skeleton && (
        <BoxExtended
          position="absolute"
          top="0"
          left="0"
          padding={getBadgeContainerPadding(variant)}
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent={horizontalBadges ? "flex-start" : "space-between"}
          opacity={1}
          zIndex={2}
        >
          <StyledStack space={1}>
            {!isMyOffer && showDiscountBadge && (
              <StyledDiscountBadge
                variant={variant}
                discountDisplay={discountDisplay}
                amount={
                  discountDisplay === discountDisplays.dollar
                    ? discountDollarValue
                    : discountPercentage
                }
                alignSelf="center"
                marginTop={isWhiskymofo ? "-12px" : getBadgeMargin(variant)}
                zIndex={2}
              />
            )}
            {showPriceDrop && <PriceDrop />}
          </StyledStack>
          {showAwardBadges && (
            <StyledBadgeStack
              marginLeft="5px"
              badges={awards}
              badgeSize={["46px", "58px"]}
              horizontal={horizontalBadges}
              hideCount
              zIndex={2}
              columns={columns}
            />
          )}
        </BoxExtended>
      )}
      <Box
        width="100%"
        backgroundColor={skeleton ? skeletonColor : cardImageBackgroundColor}
        borderRadius={skeleton ? "0" : "5px 5px 0 0"}
        position="relative"
      >
        {skeleton ? (
          <Box
            backgroundColor="white"
            width="100%"
            height="27px"
            position="absolute"
            top="-23px"
            borderRadius={"5px 5px 0 0"}
          ></Box>
        ) : (
          !isMyOffer &&
          showCountdown &&
          !isSoldOut &&
          endsAt && (
            <OfferTimer
              isExpired={isExpired}
              start={startsAt}
              end={new Date(endsAt)}
              countDays={true}
              countHours={true}
              showSecs={true}
              height="34px"
              showHorizontalBar={false}
              isSpirit={isWhiskymofo}
              urgencyMessage={!!urgencyMessage}
              spacing="0 2px"
              isLessThan24Hours={
                !isNaN(timeLeft?.days || 0) &&
                !isNaN(timeLeft?.hrs || 0) &&
                !isNaN(timeLeft?.mins || 0) &&
                !isNaN(timeLeft?.secs || 0) &&
                (timeLeft?.days || 0) === 0
              }
            />
          )
        )}
        <OfferCardImage
          skeleton={skeleton}
          isExpired={isExpired}
          useRelativeSize={!isMixed}
          image={cardImage}
          secret={secret}
          isSmallBottle={
            cardImage?.height > 120 &&
            bottleShape?.toLowerCase() === "small whisky"
          }
        />
        {roundelsImage && !detailsImageWide && (
          <Roundel
            width={["52px", "60px", "80px"]}
            height={["52px", "60px", "80px"]}
            isExpired={isExpired}
            image={roundelsImage}
            bottom={["5px", "10px", "10px"]}
            left="10px"
            zIndex="1"
          />
        )}
        {!isMixed &&
          (tags && tags.includes("Icon") ? (
            <StyledIconLogo columns={columns}>
              <StaticImage
                src="https://res.cloudinary.com/vinomofo/image/upload/v1727996346/assets/Roundels/Icon_Roundel.png"
                alt="icon logo"
                formats={["auto", "webp", "avif"]}
                loading="lazy"
              />
            </StyledIconLogo>
          ) : (
            secret && (
              <StyledBMLogo columns={columns}>
                <StaticImage
                  src="https://res.cloudinary.com/vinomofo/image/upload/v1713875491/assets/bm-logo.png"
                  alt="black market logo"
                  formats={["auto", "webp", "avif"]}
                  loading="lazy"
                />
              </StyledBMLogo>
            )
          ))}
        {isSoldOut && !isMixed && !skeleton && isMyOffer && (
          <Box position="absolute" right="0" width="60px" bottom="-15px">
            <Tooltip
              direction="top"
              delay={100}
              content={
                !isInFavourites
                  ? "Save this item to your favorites!"
                  : "Remove this from your favorites!"
              }
              styles={{
                width: "120px",
                padding: "5px",
                lineHeight: 1.4,
                fontSize: "11px",
              }}
            >
              <BellToggleV2
                data-active={isInFavourites}
                isMyOffer={isMyOffer}
                onSubscribe={handleSubscription}
                onUnsubscribe={handleUnsubscription}
                active={isInFavourites}
                setPendingRemoval={setPendingRemoval}
                pendingRemoval={pendingRemoval}
                user_id={customerId}
                style={{
                  position: "relative",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "white",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                  padding: "16px",
                  opacity: "1",
                  borderRadius: "50%",
                }}
              />
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        padding={getCardContentPadding(variant)}
        flexGrow="1"
        zIndex="13"
      >
        {skeleton && (
          <Box
            backgroundColor={skeletonColor}
            width="100%"
            height="35px"
            mb="4px"
          ></Box>
        )}
        <StyledTitle backgroundColor={skeleton ? skeletonColor : ""}>
          <Text
            as="h3"
            lineHeight="none"
            fontFamily={"Graphik"}
            letterSpacing="tight"
            color="text.heading"
            textAlign="center"
            fontSize={getNameFontSize(variant)}
            fontWeight={500}
          >
            {!skeleton &&
              (!isMyOffer ? (
                <CardAnchor
                  onClick={(event) => {
                    isRecommended
                      ? trackEvent("Recommended Product Clicked")
                      : trackEvent("Product Clicked");
                    if (!event.metaKey) {
                      event.preventDefault();
                      navigate(linkUrl, {
                        state: { queryID },
                      });
                    }
                  }}
                  href={linkUrl}
                  // skeleton={skeleton ? 1 : 0}
                  skeleton={false}
                >
                  {name}
                </CardAnchor>
              ) : (
                <>{name}</>
              ))}
          </Text>
        </StyledTitle>
        {priceDescription && (
          <Box mb={0} backgroundColor={skeleton ? skeletonColor : ""}>
            <Text
              textAlign="center"
              fontSize={getDescriptionFontSize(variant)}
              color={skeleton ? skeletonColor : "dorianGray"}
            >
              {is_wine ? priceDescription : <br />}
            </Text>
          </Box>
        )}

        <Box pt={getButtonPadding(variant)} width="100%">
          <Stack space={getButtonStackSpace(variant)}>
            <Box position="relative">
              {skeleton ? (
                <Box
                  height={["44px", "48px"]}
                  backgroundColor={skeletonColor}
                  borderRadius="24px"
                />
              ) : (
                <>
                  {!isMyOffer && (
                    <PriceButton
                      onClick={(e) => {
                        if (isRecommended)
                          trackEvent("Recommended Product Added", {
                            search_index: localStorage.getItem(
                              "algoliaIndexName"
                            ),
                            eventType: EventType.Conversion,
                            eventSubType: EventSubType.AddToCart,
                            queryID: queryID,
                          });
                        handleAddButtonClick(e);
                      }}
                      cta="Add"
                      size={getButtonSize(variant)}
                      fullPrice={!is_wine ? null : !hideRRP && rrp}
                      displayPrice={price}
                      isAvailable={!isSoldOut}
                      isLoading={productLoading[productId]}
                      ctaBackgroundColor={priceButtonBGColor}
                      color={priceButtonColor}
                      hoverColor="white"
                      notifyMeText={[
                        "Notify me",
                        "Notify me when available",
                        "Notify me when available",
                      ]}
                    />
                  )}
                </>
              )}
            </Box>
            <StyledBoxWrapper display="flex" justifyContent="space-around">
              {varietal && (
                <IconLabel
                  icon={isWhiskymofo ? <SpiritsGlassIcon /> : <VarietalIcon />}
                  label={isWhiskymofo ? firstStyle : is_wine ? varietal : ""}
                  alignSelf="center"
                  fontSize={["7px", "8px", "10px"]}
                  color={colorMap[category?.toLowerCase()] || "brand"}
                  height={"10px"}
                />
              )}
              {!skeleton && (
                <IconLabel
                  icon={
                    isWhiskymofo ? (
                      <LocationIcon />
                    ) : is_wine ? (
                      <LocationPinIcon />
                    ) : (
                      <br />
                    )
                  }
                  label={is_wine ? region : ""}
                  alignSelf="center"
                  fontSize={["7px", "8px", "10px"]}
                  height={"10px"}
                />
              )}
            </StyledBoxWrapper>
          </Stack>
        </Box>
      </Box>
      {!isMyOffer &&
        urgencyMessage &&
        formattedDisplayFlagMessage &&
        !isWhiskymofo &&
        !skeleton && (
          <OfferCtaMessage
            message={formattedDisplayFlagMessage!}
            width="100%"
            position="absolute"
            {...positionStyles}
            borderRadius={borderRadius}
            py="3px"
            isTierEligible={isTierEligible}
            isSpirit={isWhiskymofo}
            showCountdown={showCountdown && endsAt}
            isSoldOut={isSoldOut}
          />
        )}
      {!disableAllDisplayFlags && moreBtnLink && (
        <Box
          display="flex"
          flexDirection="column"
          padding={getCardContentPadding(variant)}
          flexGrow="1"
          alignItems="center"
          justifyContent="center"
          position="absolute"
          backgroundColor="rgb(255 255 255 / 88%)"
          zIndex="13"
          top={["-24px", "-38px"]}
          bottom={["-24px", "-38px"]}
          right="-8px"
          left="-8px"
        >
          <Box
            alignItems="center"
            display="flex"
            color="greenLantern"
            justifyContent="center"
            mb="15px"
          >
            <Text
              as="h2"
              lineHeight="none"
              fontFamily={"Graphik"}
              letterSpacing="tight"
              color="greenLantern"
              textAlign="center"
              fontSize={getNameFontSize(variant)}
              fontWeight={500}
            >
              {!skeleton && (
                <CardLink
                  onClick={() =>
                    trackEvent(
                      isWhiskymofo
                        ? "Show More Whisky Clicked"
                        : "Show More Wines Clicked"
                    )
                  }
                  to={moreBtnLink}
                  skeleton={skeleton ? 1 : 0}
                  color="#77b204"
                >
                  {!skeleton &&
                    (isWhiskymofo ? "Show more whisky" : "Show more wines")}
                </CardLink>
              )}
            </Text>
            <Text as="h2">{isTierEligible}</Text>
          </Box>
          <Button
            onClick={() => {
              trackEvent(
                isWhiskymofo
                  ? "Show More Whisky Clicked"
                  : "Show More Wines Clicked"
              );
              navigate(moreBtnLink);
            }}
            variant="outline"
            backgroundColor="transparent"
            borderRadius="100%"
            height="50px"
            width="50px"
          >
            <Box
              alignItems="center"
              display="flex"
              color="greenLantern"
              justifyContent="center"
            >
              <ArrowRightIcon />
            </Box>
          </Button>
        </Box>
      )}
    </StyledCard>
  );
};
